import * as Sentry from '@sentry/browser';
import { generateScore } from './generateScore';
import { validateSelections, filterInvalidActivities } from '../validation';
import { scaleScore, scaleType } from '../scaling';
import { getScaledBenchmarkCutlines } from '../getScaledBenchmarkCutlines';
import { generateLabel } from './generateLabel';
import getSeason from '../getSeason';
import { getActivityStatus } from '../../../../services/displayStatus';
import getArmScore from '../getArmScore'
import { isPendingAssessment } from '../pendingAssessment';
import { getPR } from '../../functions/getPR';

//###TODO: This needs to be pegged to the lowest possible ARM score. Find a good place we can share with the backend
// NOTE: Left the calculation decomposed because I want the lowest negative value minus a small buffer.
const MIN_NEGATIVE_VALUE = (-0.4 - 0.1);

// TODO consider refactoring out common code between tracking and benchmark
export async function generateBenchmarkReport(activities, metricId, scaleId, benchmarkId, customBenchmarks, selectedLocale, students = [], isBenchmarkOnly = false,window=null, selectedStatePercentileCuts) {
  // check for valid selections and activities
  if(!activities || activities.length === 0 || !validateSelections(metricId, scaleId, benchmarkId) && metricId !== "ARM") {
    return {
      scores: [],
      cutoffs: {},
      displayCutoffs: {},
      cutlines: [],
      selectedBenchmarkGrade: benchmarkId?.grade,
      selectedWindow: window?.id,
      selectedLocale,
      selectedMetric: metricId,
    };
  }

  // mapping studentId to whether they are pending status
  const isPendingMap = {}
  for (const activity of activities) {
    const studentId = activity.student.id;
    if (studentId in isPendingMap) continue; // only check the latest activity for each student
    isPendingMap[studentId] = isPendingAssessment(activity);
  }

  const newActivities = isBenchmarkOnly
    ? activities.filter(({ story }) => !story.tags || story.tags.includes("BENCHMARK") || story.tags.length == 0)
    : activities;
  // filter to most recent valid activity per student
  let validScoredActivities = filterInvalidActivities(newActivities,true,true)
  .filter((activity, i, activities) => i === activities.findIndex(a => a.studentId === activity.studentId));
  // add the missing students to the activities by creating dummy activity object filled with student's info
  // the goal is to fill in students with no current valid assessments / scores for rendering
  let unassessedStudentActivities = students.reduce( (acc, student) => {

    // do not include students already included in validScoredActivities
    if(validScoredActivities.find( a => a.student.id === student.id)) return acc

    // add display status for labeling pre-readers
    const studentActivities = newActivities.filter( activity => activity.student.id === student.id )
    const displayStatus = studentActivities.length ? studentActivities[0].displayStatus : ""
    const status = studentActivities.length ? studentActivities[0].status : "";

    // build a mock activity object for rendering pending placeholder UI in report
    return [...acc, {
      student,
      student_id: student.id,
      activityId: `none_activity_${student.id}`,
      children: [],
      score: 0,
      scores: {
        areaScore: 0,
        decodeScore: 0,
        esriScore: 0,
        phonAwareScore: 0,
        vocabSize: 0,
        wcpmScore: 0,
        armScore: null
      },
      tags: [],
      type: 'assessment',
      displayStatus,
      status,
    }]
  }, [])

  // combine all scored and unscored and unassessed student activities for display
  let filteredActivities = [...validScoredActivities, ...unassessedStudentActivities]

  // TODO enable this code block to add NWF and spelling
  /*
  let enrichedFilteredActivities = [];
  filteredActivities.forEach(assessment => {
    if(assessment.children) {
      // find nwf
      let nwf = assessment.children.find(act => act.type == 'nwf');
      if(nwf && nwf.scores && nwf.scores.rawScore) {
        assessment.scores.nwf = nwf.scores.rawScore;
      }
      // find spelling
      let spell = assessment.children.find(act => act.type == 'spelling');
      if(spell && spell.scores && spell.scores.rawScore) {
        assessment.scores.spelling = spell.scores.rawScore;
      }
    }
    // push the enriched activity
    enrichedFilteredActivities.push(assessment);
  });
  */

  // create benchmark scores
  let scores = filteredActivities.reduce((scores, activity) => {
    let nonScoredLabel = "Unassessed"
    let matchingSiblingActivity;
    if (activity.displayStatus === "prereader") {
      if(!activity.scores){
        activity.scores = {}
      }
      activity.scores.wcpmScore = 0;
      nonScoredLabel = "Early Reader"
      const dyslexiaChildActivity = activity.children.find(act => act.type === "dyslexia")

      const targetStatuses = ['underReview']; // replace with actual status values

      if (activity.siblings && Array.isArray(activity.siblings)) {
        matchingSiblingActivity = activity.siblings.find(sibling =>
        sibling.type === "dyslexia" &&
        targetStatuses.includes(sibling.status)
      )}

      if ((dyslexiaChildActivity && dyslexiaChildActivity.status === "underReview") || (matchingSiblingActivity && matchingSiblingActivity.status === "underReview")) nonScoredLabel = "Under Review"
      if (dyslexiaChildActivity && dyslexiaChildActivity.displayStatus === "reassess") nonScoredLabel = "Reassess"
    }

    activity.scores.armScore = getArmScore(activity.student.id, activity, selectedLocale);
    let score = generateScore(activity, metricId, scaleId, false, selectedLocale);

    // let null or zero scores be added to the scores for rendering student benchmark charts without assessments
    if(score !== undefined) {
      // Need to override the status if we're able to display a score. The logic is that if we're able to display a score then
      //  we should do so.
      // TODO: We need a method that ignores child status.
      let rowStatus = getActivityStatus(activity).id;
      if(score !== null && rowStatus === 'UNDER_REVIEW'){
        rowStatus = `COMPLETE`;
      }

      // In order to handle 0 as a valid score for ARM and Lexile, we need an explicit null check
      let labeledScore = ((['ARM','Lexile'].includes(scaleId) && score !== null) || (score)) ? generateLabel(score, metricId, scaleId) : nonScoredLabel;
      // Have to duplicate bcause labeled score includes the metric label which we don't need or want on class progress report
      let displayScore = ((['ARM','Lexile'].includes(scaleId) && score !== null) || (score)) ? generateLabel(score, metricId, scaleId, false) : null;
      if(metricId === 'ARM' && !activity.activityId.startsWith('none_activity_') && labeledScore === nonScoredLabel && nonScoredLabel != 'Under Review'){
        labeledScore = "No Arm Score"
      }

      // get the percentile rank
      const PR = getPR({
        score,
        scale: scaleId,
        grade: benchmarkId?.grade,
        language: selectedLocale,
        window: window?.id,
      });

      scores.push({
        ...activity.student,  //NOTE: this *must* come before the assignment of id below this because otherwise student.id overwrites the id field
        id: activity.activityId,  //TODO: rename this activityID to avoid confusion and avoid bugs like the one detailed above
        score,
        displayScore,
        level: '',
        date: activity.createdAt,
        GRL: scaleScore(activity.scores.wcpmScore, scaleType.Guided_Reading_Level, activity,selectedLocale),
        labeledScore,
        status: rowStatus,
        PR,
        isPending: isPendingMap[activity.student.id]
      });
    }
    return scores;
  }, []);

  // generate cutlines
  // TODO: improve error handling, and null benchmark state passing
  const windowToSeason = {
    BOY: "fall",
    MOY: "winter",
    EOY: "spring"
  }
  const activeWindow = window && window.id ? windowToSeason[window.id] : getSeason();
  let { cutoffs, cutlines } = getScaledBenchmarkCutlines(benchmarkId,scaleId,activeWindow,customBenchmarks, selectedLocale, selectedStatePercentileCuts);
  // Have to duplicate this because we are scaling the cutoffs and cutlines for display on the benchmark report, but this is also used by the class progress report
  let { cutoffs: displayCutoffs, cutlines: displayCutlines } = getScaledBenchmarkCutlines(benchmarkId, scaleId, activeWindow, customBenchmarks, selectedLocale);

  // Rescale lexiles and arm
  if((scaleId == scaleType.Lexile) || (scaleId == scaleType.ARM)) {
    // Find the min score
    const findMin = (min, row) => {
      if(row.score < min) {
        return row.score;
      }
      return min;
    }

    const findMax = (max, row) => {
      if(row.score > max) {
        return row.score;
      }
      return max;
    }

    let min = scores.reduce(findMin, 0);
    let max = scores.reduce(findMax,0);

    // If min is negative, rescale everything
    if(min <= 0) {
      //Need to special case so that the minimum value doesn't have a width of 0
      // Base this on a % of the maximum value
      min -= max*0.1;

      scores = scores.map(row => {
        //If a score is not at the minimum negative value already, then adjust
        // This accounts for "dummy values" for unassessed students above
        // Don't scale display score which is used by class progress report
        if(row.score !== null){
          row.displayScore = row.score;
          row.score = row.score - min;
        }else{
          row.score = min;
        }

        return row;
      });
      // TODO eliminate the whole concept of cutoffs, and use a map
      cutoffs.POOR = cutoffs.POOR - min;
      cutoffs.AVERAGE = cutoffs.AVERAGE - min;
      cutoffs.GOOD = cutoffs.GOOD - min;
      cutlines[0].value = cutlines[0].value - min;
      cutlines[1].value = cutlines[1].value - min;
      cutlines[2].value = cutlines[2].value - min;
    }
  }

  //We need to roughly sort the scores here by status and by null/non-null so that scores of 0 show up before nulls
  // when the report sorts.
  scores = roughSort(scores);

  return {
    scores,
    cutlines,
    displayCutoffs,
    cutoffs,
    selectedLocale,
    selectedBenchmarkGrade: benchmarkId?.grade,
    selectedBenchmarkName: benchmarkId?.label,
    selectedWindow: window?.id,
    selectedMetric: metricId,
  };
}

function roughSort(scores){
  return scores.sort((a,b)=>{
    //Keep completed scores above non-completed
    if((a.status === 'COMPLETE' || a.status === 'PRE-READER') && b.status !== 'COMPLETE' && b.status !== 'PRE-READER') return -1;
    if((b.status === 'COMPLETE' || b.status === 'PRE-READER') && a.status !== 'COMPLETE' && b.status !== 'PRE-READER') return 1;
    //Sort any score (including 0) above null
    if(a.score !== null && b.score === null) return -1;
    if(b.score !== null && a.score === null) return 1;
    if(a.score === b.score) return 0;
    if(a.score > b.score) return -1;
    return 1;
   })
}

export default {
  generateBenchmarkReport,
}